import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CreditCard, RefreshCw, RotateCcw, ExternalLink, Search, Loader2, Copy } from 'lucide-react';
import { FlightDepositsPanel } from './FlightDepositsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  PAID: 'bg-emerald-100 text-emerald-700',
  PENDING: 'bg-amber-100 text-amber-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-slate-200 text-slate-600',
  REFUNDED: 'bg-purple-100 text-purple-700',
  PARTIALLY_REFUNDED: 'bg-purple-50 text-purple-600',
};

const money = (v, c = 'GBP') => `${c === 'GBP' ? '£' : c + ' '}${Number(v || 0).toFixed(2)}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export const PaymentsPanel = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [env, setEnv] = useState('production');
  const [dashboardUrl, setDashboardUrl] = useState('https://business.revolut.com/merchant');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundConfirmed, setRefundConfirmed] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [syncing, setSyncing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status !== 'all') params.status = status;
      const { data } = await axios.get(`${API_URL}/api/admin/payments`, { params, withCredentials: true });
      setRows(data.payments || []);
      setStats(data.stats || {});
      setEnv(data.revolut_env);
      setDashboardUrl(data.dashboard_url);
    } catch (e) {
      toast.error(e.response?.status === 401 || e.response?.status === 403 ? 'Admin login required' : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRefund = (row) => {
    setRefundTarget(row);
    setRefundAmount(String(row.refundable_amount));
    setRefundReason('');
    setRefundConfirmed(false);
  };

  const doRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (Number.isNaN(amt) || amt <= 0 || amt > refundTarget.refundable_amount + 0.001) {
      toast.error(`Enter an amount between £0.01 and ${money(refundTarget.refundable_amount)}`);
      return;
    }
    setRefunding(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/payments/${refundTarget.order_id}/refund`, { amount: amt, reason: refundReason }, { withCredentials: true });
      toast.success(`Refunded ${money(amt)} on ${refundTarget.booking_id} — now ${data.payment_status.replace('_', ' ')}`);
      setRefundTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const sync = async (row) => {
    setSyncing(row.order_id);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/payments/${row.order_id}/sync`, {}, { withCredentials: true });
      toast.success(`Revolut says: ${data.revolut_state}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success('Copied'); };

  const statCard = (label, key, cls, testKey = key.toLowerCase()) => (
    <Card className={`${cls} text-white`}>
      <CardContent className="p-4">
        <p className="text-white/80 text-sm">{label}</p>
        <p className="text-2xl font-bold" data-testid={`payments-stat-${testKey}`}>{money(stats[key]?.amount)}</p>
        <p className="text-white/70 text-xs">{stats[key]?.count || 0} payment{(stats[key]?.count || 0) === 1 ? '' : 's'}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="payments-panel">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statCard('Paid', 'PAID', 'bg-emerald-600')}
        {statCard('Pending', 'PENDING', 'bg-amber-500')}
        {statCard('Failed / Cancelled', 'FAILED_OR_CANCELLED', 'bg-red-500', 'failed')}
        {statCard('Refunded', 'REFUNDED_TOTAL', 'bg-purple-600', 'refunded')}
      </div>

      <FlightDepositsPanel />

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <CreditCard className="h-5 w-5 text-orange-600" /> Payments Ledger
            <Badge className={env === 'production' ? 'bg-emerald-600' : 'bg-amber-500'} data-testid="payments-env-badge">Revolut {env}</Badge>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="PNR, email, order id" className="pl-8 w-56" data-testid="payments-search-input" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-44" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.keys(STATUS_STYLES).map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} data-testid="payments-refresh-btn"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
            <Button asChild variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50">
              <a href={dashboardUrl} target="_blank" rel="noreferrer" data-testid="open-revolut-link"><ExternalLink className="h-4 w-4 mr-2" />Open Revolut</a>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>PNR / Ref</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Order id</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500"><Loader2 className="h-5 w-5 inline animate-spin mr-2" />Loading payments…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500" data-testid="payments-empty">No payments match.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.order_id} data-testid={`payment-row-${r.order_id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{when(r.created_at)}</TableCell>
                  <TableCell className="font-mono font-bold text-slate-800">{r.booking_id}</TableCell>
                  <TableCell><Badge variant="outline">{r.product}</Badge>{r.payment_plan && r.payment_plan !== 'FULL' && <Badge className="ml-1 bg-orange-100 text-orange-800 border-0" data-testid={`payment-plan-${r.order_id}`}>{r.payment_plan}</Badge>}</TableCell>
                  <TableCell className="text-sm">
                    <div className="text-slate-800">{r.customer_name || '—'}</div>
                    <div className="text-xs text-slate-500">{r.customer_email}</div>
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {money(r.amount, r.currency)}
                    {r.refunded_amount > 0 && <div className="text-xs text-purple-600">−{money(r.refunded_amount, r.currency)} refunded</div>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_STYLES[r.payment_status] || 'bg-slate-100 text-slate-600'} border-0`} data-testid={`payment-status-${r.order_id}`}>{(r.payment_status || '').replace('_', ' ')}</Badge>
                    {r.failure_reason && <div className="text-xs text-red-500 mt-1">{r.failure_reason}</div>}
                  </TableCell>
                  <TableCell>
                    <button onClick={() => copy(r.order_id)} className="font-mono text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" title="Copy order id">
                      {r.order_id.slice(0, 8)}… <Copy className="h-3 w-3" />
                    </button>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => sync(r)} disabled={syncing === r.order_id} title="Re-check status with Revolut" data-testid={`sync-${r.order_id}`}>
                      <RefreshCw className={`h-4 w-4 mr-1 ${syncing === r.order_id ? 'animate-spin' : ''}`} /> Sync
                    </Button>
                    {r.refundable_amount > 0 && (
                      <Button size="sm" onClick={() => openRefund(r)} className="bg-purple-600 hover:bg-purple-700 text-white ml-1" data-testid={`refund-${r.order_id}`}>
                        <RotateCcw className="h-4 w-4 mr-1" /> Refund
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund {refundTarget?.booking_id}</DialogTitle>
            <DialogDescription>
              Money goes straight back to the customer's card via Revolut. Up to {money(refundTarget?.refundable_amount, refundTarget?.currency)} can be refunded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Amount (£)</label>
              <Input type="number" step="0.01" min="0.01" max={refundTarget?.refundable_amount} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Reason (internal note)</label>
              <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="e.g. customer cancelled" data-testid="refund-reason-input" />
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 bg-purple-50 border border-purple-200 rounded-lg p-3 cursor-pointer">
              <input type="checkbox" checked={refundConfirmed} onChange={(e) => setRefundConfirmed(e.target.checked)} className="mt-0.5" data-testid="refund-confirm-checkbox" />
              <span>I understand this sends <strong>real money</strong> back to the customer's card immediately and cannot be undone.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)}>Cancel</Button>
            <Button onClick={doRefund} disabled={refunding || !refundConfirmed} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="refund-confirm-btn">
              {refunding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Refund {money(parseFloat(refundAmount) || 0)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
